import React, { useState, useEffect } from "react"
import { navigate } from "vike/client/router"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import DataTable from "aldoo-ra/DataTable"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import AgentEditor from "./components/AgentEditor"
import { AgentsCRUD } from "./api"

const OUTCOME_COLORS = {
  true:  "bg-admin_success/15 text-admin_success",
  false: "bg-admin_neutral_1 text-admin_text_secondary"
}

function getParams() {
  return new URLSearchParams(window.location.search)
}

function setParams(updates) {
  const params = getParams()
  Object.entries(updates).forEach(([k, v]) => {
    if (v) params.set(k, v)
    else params.delete(k)
  })
  const qs = params.toString()
  const newPath = window.location.pathname + (qs ? `?${qs}` : "")
  if (window.location.pathname + window.location.search !== newPath)
    navigate(newPath, { keepScrollPosition: true })
}

export default function AgentBrowser() {
  const [view, setView]           = useState("list")
  const [selected, setSelected]   = useState(null)
  const [initialTab, setInitialTab] = useState("details")
  const [refreshKey, setRefreshKey] = useState(0)
  const refresh = () => setRefreshKey((k) => k + 1)

  // On mount: read ?agent= and ?tab= from URL
  useEffect(() => {
    const params = getParams()
    const agentId = params.get("agent")
    const tab     = params.get("tab")
    if (!agentId) return

    AgentsCRUD.Get({ match: { agentId } }).then((res) => {
      const agent = Array.isArray(res) ? res[0] : res?.data?.[0]
      if (!agent) return
      setSelected(agent)
      setInitialTab(tab || "details")
      setView("edit")
    })
  }, [])

  const handleEdit = (agent, tab = "details") => {
    setSelected(agent)
    setInitialTab(tab)
    setView("edit")
    setParams({ agent: agent.agentId, tab })
  }

  const handleClose = () => {
    setView("list")
    setSelected(null)
    setParams({ agent: null, tab: null })
    refresh()
  }

  const handleDelete = (agent) => {
    showPromptDialog({
      title:       "Delete Agent",
      message:     `Delete agent "${agent.name}" (${agent.agentId})? This cannot be undone.`,
      confirmText: "Delete",
      confirmVariant: "error_m",
      onConfirm: async () => {
        const res = await AgentsCRUD.HardDelete(agent)
        if (!res?.error) {
          showNotification({ message: "Agent deleted", className: "bg-admin_primary text-admin_text_inv" })
          refresh()
        } else {
          showNotification({ message: res.error || "Delete failed", className: "bg-admin_error text-white" })
        }
      }
    })
  }

  const columns = [
    {
      id: "name",
      header: "Name",
      headerClassName: "w-[25%]",
      render: (_, row) => (
        <div>
          <p className="font-medium text-admin_text text-sm">{row.name}</p>
          <p className="text-xs text-admin_text_secondary">{row.agentId}</p>
        </div>
      )
    },
    {
      id: "roleId",
      header: "Role",
      headerClassName: "w-[15%]",
      render: (v) => <span className="text-sm text-admin_text">{v}</span>
    },
    {
      id: "aiModel",
      header: "Model",
      headerClassName: "w-[18%]",
      render: (v) => <span className="text-xs bg-admin_neutral_1 px-2 py-1 rounded">{v}</span>
    },
    {
      id: "skills",
      header: "Skills",
      headerClassName: "w-[10%]",
      render: (v) => <span className="text-sm text-admin_text">{v?.length || 0}</span>
    },
    {
      id: "enabled",
      header: "Status",
      headerClassName: "w-[10%]",
      render: (v) => (
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${OUTCOME_COLORS[v]}`}>
          {v ? "Active" : "Disabled"}
        </span>
      )
    },
    {
      id: "actions",
      header: "",
      headerClassName: "w-[12%]",
      render: (_, row) => (
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" size="sm" onClick={() => handleEdit(row)}>Edit</Button>
          <Button variant="error_m"   size="sm" onClick={() => handleDelete(row)}>Delete</Button>
        </div>
      )
    }
  ]

  if (view === "create") {
    return (
      <div className="flex flex-col h-full">
        <AgentEditor
          agent={null}
          initialTab="details"
          onTabChange={(tab) => setParams({ tab })}
          onSave={handleClose}
          onCancel={handleClose}
        />
      </div>
    )
  }

  if (view === "edit") {
    return (
      <div className="flex flex-col h-full">
        <AgentEditor
          agent={selected}
          initialTab={initialTab}
          onTabChange={(tab) => setParams({ agent: selected?.agentId, tab })}
          onSave={(res) => { if (res && !res.error) setSelected(res) }}
          onCancel={handleClose}
        />
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <Typography variant="h2">Agents</Typography>
        <Button variant="primary" onClick={() => { setView("create"); setParams({ agent: "new", tab: "details" }) }}>+ New Agent</Button>
      </div>

      <DataTable
        refreshKey={refreshKey}
        renders={columns}
        apiMethod={AgentsCRUD.Get}
        requestFields={{}}
        onRowClick={(row) => handleEdit(row)}
      />
    </div>
  )
}
